import React from 'react';
import { compileComponent } from './compileComponent';
import { UNIVERSAL_FUNCTIONS } from './universalFunctions';
import type { UxGenieUIEngineProps } from './types';

// Renders an AI-generated UI component. Injects host functions and passes
// the data prop so UI-mode components (FormComponent = ({ data }) => ...) can
// access live data both as a prop and as a free variable in scope.
//
// Standalone-safe: only imports React, compileComponent, and types.
export function UxGenieUIEngine({ component, data, hostFunctions = {} }: UxGenieUIEngineProps) {
  const mergedHostFunctions = React.useMemo(
    () => ({ ...UNIVERSAL_FUNCTIONS, ...hostFunctions }),
    [hostFunctions]
  );

  const compilationResult = React.useMemo(() => {
    try {
      const ComponentToRender = compileComponent(component, {
        hostFunctions: mergedHostFunctions,
        data,
        formConfig: null,
      });
      return { ComponentToRender, error: null };
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unknown error while compiling generated code.';
      return {
        ComponentToRender: () => (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            Error rendering component. {message}
          </div>
        ),
        error: message,
      };
    }
  }, [component, mergedHostFunctions, data]);

  const { ComponentToRender, error } = compilationResult;

  return (
    <div className="h-full w-full min-w-0 overflow-auto rounded-[24px] bg-content1 p-4 lg:p-6">
      {error && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Falling back to the error renderer because the generated component
          could not be executed.
        </div>
      )}
      <ComponentToRender data={data} />
    </div>
  );
}

export default UxGenieUIEngine;
